/**
 * buffers — os dois anéis de memória que dão contexto ao relato de bug.
 *
 * NADA DISTO É INSTALADO SEM CLAIM. `instalar()` é chamado uma única vez, pelo
 * host, depois que o gate confirma que o uid está habilitado. Para todo o resto
 * da base o módulo é código morto: nenhum listener, nenhum embrulho, nenhum
 * array alocado, nenhuma escrita.
 *
 * NADA É ENVIADO ENQUANTO NÃO SE TOCA EM ENVIAR. Os dois anéis são arrays de
 * tamanho fixo em memória. Sem localStorage, sem Firestore, sem log remoto —
 * o histórico de custo deste projeto não comporta telemetria contínua, e um
 * relato acontece algumas vezes por dia.
 *
 * O QUE NUNCA ENTRA: corpo de requisição, cabeçalho, token, e a query string da
 * URL. Um `?token=` ou um `?cpf=` numa URL viraria dado sensível gravado em
 * documento que o painel lê — e a URL sem query já identifica o endpoint, que é
 * o que serve para depurar.
 */

const MAX_ERROS = 20;
const MAX_REDE = 10;
// Stack de React em produção é longo e repetitivo; 2 KB pega o topo, que é onde
// está a informação.
const MAX_TEXTO = 2048;

const erros = [];
const rede = [];

let instalado = false;
let desinstalar = null;

const corta = (v) => {
  const s = typeof v === 'string' ? v : String(v ?? '');
  return s.length > MAX_TEXTO ? `${s.slice(0, MAX_TEXTO)}…[cortado]` : s;
};

const agora = () => new Date().toISOString();

function empurrar(anel, limite, item) {
  anel.push(item);
  while (anel.length > limite) anel.shift();
}

/**
 * Registra um erro de console/runtime no anel.
 *
 * NO-OP SEM INSTALAÇÃO. Quem chama daqui de fora (o hook do
 * firebaseFunctionCaller) chama sempre, sem saber do gate — e para quem não tem
 * claim o custo é uma comparação booleana. É isto que garante que a base inteira
 * não alimenta anel nenhum.
 */
export function registrarErro({ tipo, mensagem, stack, origem }) {
  if (!instalado) return;
  empurrar(erros, MAX_ERROS, {
    TIPO: tipo || 'erro',
    MENSAGEM: corta(mensagem),
    STACK: stack ? corta(stack) : null,
    ORIGEM: origem || null,
    EM: agora(),
  });
}

/**
 * Registra uma falha de rede no anel. No-op sem instalação, como `registrarErro`.
 * A URL chega aqui já sem query string (ver `semQuery`).
 */
export function registrarFalhaRede({ metodo, url, status, codigo, requestId, detalhe }) {
  if (!instalado) return;
  empurrar(rede, MAX_REDE, {
    METODO: metodo || null,
    URL: url || null,
    STATUS: status ?? null,
    CODIGO: codigo || null,
    REQUEST_ID: requestId || null,
    DETALHE: detalhe ? corta(detalhe) : null,
    EM: agora(),
  });
}

/** Tira a query string e o fragmento; mantém origem + caminho. */
export function semQuery(url) {
  const bruto = String(url || '');
  if (!bruto) return null;
  try {
    const u = new URL(bruto, typeof window !== 'undefined' ? window.location.origin : undefined);
    return `${u.origin}${u.pathname}`;
  } catch {
    // URL relativa esquisita ou objeto Request já convertido: corta na mão.
    return bruto.split('?')[0].split('#')[0];
  }
}

export const lerErros = () => erros.slice();
export const lerFalhasRede = () => rede.slice();

/** Só para teste: zera os dois anéis. */
export function limpar() {
  erros.length = 0;
  rede.length = 0;
}

/**
 * Instala os coletores. Idempotente — chamar duas vezes não embrulha duas vezes.
 * Devolve a função que desfaz tudo (usada se a config for desligada em runtime).
 */
export function instalar() {
  if (instalado || typeof window === 'undefined') return desinstalar || (() => {});
  instalado = true;

  const aoErro = (evento) => {
    registrarErro({
      tipo: 'window.onerror',
      mensagem: evento?.message || 'erro sem mensagem',
      stack: evento?.error?.stack,
      origem: evento?.filename ?
        `${evento.filename}:${evento.lineno}:${evento.colno}` : null,
    });
  };

  const aoRejeitar = (evento) => {
    const r = evento?.reason;
    registrarErro({
      tipo: 'unhandledrejection',
      mensagem: r?.message || (typeof r === 'string' ? r : JSON.stringify(r ?? null)),
      stack: r?.stack,
      origem: r?.code || null,
    });
  };

  window.addEventListener('error', aoErro);
  window.addEventListener('unhandledrejection', aoRejeitar);

  // console.error embrulhado PRESERVANDO o original: o app continua logando
  // igual, e o DevTools continua mostrando a mesma coisa. Se o embrulho
  // lançasse, levaria junto um console.error de verdade — daí o try.
  const errOriginal = window.console?.error;
  if (typeof errOriginal === 'function') {
    window.console.error = function relatoBugConsoleError(...args) {
      try {
        const primeiro = args[0];
        registrarErro({
          tipo: 'console.error',
          mensagem: args
              .map((a) => (a instanceof Error ? a.message : (
                typeof a === 'string' ? a : (() => {
                  try {
                    return JSON.stringify(a);
                  } catch {
                    return String(a);
                  }
                })()
              )))
              .join(' '),
          stack: primeiro instanceof Error ? primeiro.stack : null,
        });
      } catch { /* nunca atrapalhar o log de verdade */ }
      return errOriginal.apply(this, args);
    };
  }

  // UM embrulho de fetch, aqui. A alternativa era editar os 18 arquivos que
  // chamam fetch direto — mais diff, mais chance de esquecer um, e teria de ser
  // desfeito se o recurso saísse.
  const fetchOriginal = window.fetch;
  if (typeof fetchOriginal === 'function') {
    window.fetch = async function relatoBugFetch(entrada, init) {
      const metodo = String(init?.method || entrada?.method || 'GET').toUpperCase();
      const url = semQuery(typeof entrada === 'string' ? entrada : entrada?.url);
      try {
        const resposta = await fetchOriginal.call(this, entrada, init);
        if (!resposta.ok) {
          registrarFalhaRede({
            metodo,
            url,
            status: resposta.status,
            // Cabeçalhos de correlação são os únicos que lemos, e só estes.
            requestId: resposta.headers?.get?.('x-request-id') ||
              resposta.headers?.get?.('x-cloud-trace-context') || null,
          });
        }
        return resposta;
      } catch (err) {
        // Falha de transporte (offline, DNS, CORS): não há status.
        registrarFalhaRede({
          metodo, url, status: null, codigo: 'network_error', detalhe: err?.message,
        });
        throw err;
      }
    };
  }

  desinstalar = () => {
    window.removeEventListener('error', aoErro);
    window.removeEventListener('unhandledrejection', aoRejeitar);
    if (typeof errOriginal === 'function') window.console.error = errOriginal;
    if (typeof fetchOriginal === 'function') window.fetch = fetchOriginal;
    instalado = false;
    desinstalar = null;
  };

  return desinstalar;
}

export const estaInstalado = () => instalado;

export const LIMITES = { MAX_ERROS, MAX_REDE, MAX_TEXTO };
